import { useMemo } from 'react';
import type { ApolloClient } from '@apollo/client';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import type { RegisterAbortFunction } from '@dr-pam/common-components/Utils/AbortUtils';
import ApolloUtils from '@dr-pam/common-components/Utils/ApolloUtils';
import useAbortRegistry from '@dr-pam/common-components/Hooks/useAbortRegistry';
import type { AbortableRequest } from '@dr-pam/common-components/Utils/FetchUtils';
import type {
	CreateUpgradePathDiscountMutation,
	CreateUpgradePathDiscountMutationVariables,
	CreateUpgradePromptMutation,
	CreateUpgradePromptMutationVariables,
	DeleteUpgradePathDiscountMutation,
	DeleteUpgradePathDiscountMutationVariables,
	DeleteUpgradePromptMutation,
	DeleteUpgradePromptMutationVariables,
	UpgradePathDiscountCreateInput,
	UpgradePathDiscountUpdateInput,
	UpgradePathDiscountListFragment,
	UpgradePathDiscountQuery,
	UpgradePathDiscountQueryVariables,
	UpgradePathDiscountSingleFragment,
	UpgradePathDiscountsQuery,
	UpgradePathDiscountsQueryVariables,
	UpgradePromptCreateInput,
	UpgradePromptUpdateInput,
	UpgradePromptListFragment,
	UpgradePromptQuery,
	UpgradePromptQueryVariables,
	UpgradePromptSingleFragment,
	UpgradePromptsQuery,
	UpgradePromptsQueryVariables,
	UpdateUpgradePathDiscountMutation,
	UpdateUpgradePathDiscountMutationVariables,
	UpdateUpgradePromptMutation,
	UpdateUpgradePromptMutationVariables,
	UpgradeSubscriptionTypesQuery,
	UpgradeSubscriptionTypesQueryVariables,
} from '../graphql/graphql';

export type SubscriptionTypeOption = NonNullable<UpgradeSubscriptionTypesQuery['productSubscriptionTypes']>[number];

// This service provides a clean GraphQL interface for managing upgrade paths and prompts in the admin dashboard.
// It centralizes all GraphQL fragments, queries, and mutations so the admin UI stays in sync with schema changes.
// The upgrade system allows marketing teams to configure time-based discounts and prompts for trial users
// without requiring code deployments, enabling rapid A/B testing and campaign adjustments.
export const GQL_FRAG_UPGRADE_SUBSCRIPTION_TYPE = gql`
	fragment UpgradeSubscriptionType on ProductSubscriptionType {
		id
		name
		productId
		isTrialSubscription
		durationInDays
		product {
			id
			name
			shortName
		}
	}
`;

export const GQL_FRAG_UPGRADE_PATH_DISCOUNT_LIST = gql`
	${GQL_FRAG_UPGRADE_SUBSCRIPTION_TYPE}
	fragment UpgradePathDiscountList on UpgradePathDiscount {
		id
		name
		description
		applyCreditForExistingPurchase
		creditAllPriorPayments
		discountPercentage
		discountAmountInCents
		daysAfterPurchaseMin
		daysAfterPurchaseMax
		isActive
		upgradeDurationMode
		fromProductSubscriptionTypeId
		fromProductSubscriptionType {
			...UpgradeSubscriptionType
		}
		# Target subscription types
		targetProducts {
			id
			productSubscriptionTypeId
			productSubscriptionType {
				...UpgradeSubscriptionType
			}
		}
		_count {
			upgradePrompts
		}
	}
`;

export const GQL_FRAG_UPGRADE_PATH_DISCOUNT_SINGLE = gql`
	${GQL_FRAG_UPGRADE_SUBSCRIPTION_TYPE}
	fragment UpgradePathDiscountSingle on UpgradePathDiscount {
		id
		name
		description
		applyCreditForExistingPurchase
		creditAllPriorPayments
		discountPercentage
		discountAmountInCents
		daysAfterPurchaseMin
		daysAfterPurchaseMax
		isActive
		upgradeDurationMode
		fromProductSubscriptionTypeId
		fromProductSubscriptionType {
			...UpgradeSubscriptionType
		}
		# Target subscription types
		targetProducts {
			id
			productSubscriptionTypeId
			productSubscriptionType {
				...UpgradeSubscriptionType
			}
		}
	}
`;

export const GQL_FRAG_UPGRADE_PROMPT_LIST = gql`
	fragment UpgradePromptList on UpgradePrompt {
		id
		name
		promptType
		frequency
		showFromDayAfterPurchase
		showUntilDayAfterPurchase
		isActive
		priority
		title
		message
		ctaText
		upgradePathDiscountId
		upgradePathDiscount {
			id
			name
		}
		_count {
			engagements
		}
	}
`;

export const GQL_FRAG_UPGRADE_PROMPT_SINGLE = gql`
	fragment UpgradePromptSingle on UpgradePrompt {
		id
		name
		promptType
		frequency
		showFromDayAfterPurchase
		showUntilDayAfterPurchase
		isActive
		priority
		title
		message
		ctaText
		upgradePathDiscountId
	}
`;

export const GQL_GET_UPGRADE_PATH_DISCOUNTS = gql`
	${GQL_FRAG_UPGRADE_PATH_DISCOUNT_LIST}
	query UpgradePathDiscounts {
		upgradePathDiscounts(orderBy: [{ name: asc }]) {
			...UpgradePathDiscountList
		}
	}
`;

export const GQL_GET_UPGRADE_PATH_DISCOUNT = gql`
	${GQL_FRAG_UPGRADE_PATH_DISCOUNT_SINGLE}
	query UpgradePathDiscount($upgradePathDiscountId: String!) {
		upgradePathDiscount(where: { id: $upgradePathDiscountId }) {
			...UpgradePathDiscountSingle
		}
	}
`;

export const GQL_CREATE_UPGRADE_PATH_DISCOUNT = gql`
	${GQL_FRAG_UPGRADE_PATH_DISCOUNT_SINGLE}
	mutation CreateUpgradePathDiscount($upgradePathDiscount: UpgradePathDiscountCreateInput!) {
		createOneUpgradePathDiscount(data: $upgradePathDiscount) {
			...UpgradePathDiscountSingle
		}
	}
`;

export const GQL_UPDATE_UPGRADE_PATH_DISCOUNT = gql`
	${GQL_FRAG_UPGRADE_PATH_DISCOUNT_SINGLE}
	mutation UpdateUpgradePathDiscount(
		$upgradePathDiscountId: String!
		$upgradePathDiscount: UpgradePathDiscountUpdateInput!
	) {
		updateOneUpgradePathDiscount(where: { id: $upgradePathDiscountId }, data: $upgradePathDiscount) {
			...UpgradePathDiscountSingle
		}
	}
`;

export const GQL_DELETE_UPGRADE_PATH_DISCOUNT = gql`
	mutation DeleteUpgradePathDiscount($upgradePathDiscountId: String!) {
		deleteOneUpgradePathDiscount(where: { id: $upgradePathDiscountId }) {
			id
		}
	}
`;

export const GQL_GET_UPGRADE_PROMPTS = gql`
	${GQL_FRAG_UPGRADE_PROMPT_LIST}
	query UpgradePrompts {
		upgradePrompts(orderBy: [{ priority: desc }, { name: asc }]) {
			...UpgradePromptList
		}
	}
`;

export const GQL_GET_UPGRADE_PROMPT = gql`
	${GQL_FRAG_UPGRADE_PROMPT_SINGLE}
	query UpgradePrompt($upgradePromptId: String!) {
		upgradePrompt(where: { id: $upgradePromptId }) {
			...UpgradePromptSingle
		}
	}
`;

export const GQL_CREATE_UPGRADE_PROMPT = gql`
	${GQL_FRAG_UPGRADE_PROMPT_SINGLE}
	mutation CreateUpgradePrompt($upgradePrompt: UpgradePromptCreateInput!) {
		createOneUpgradePrompt(data: $upgradePrompt) {
			...UpgradePromptSingle
		}
	}
`;

export const GQL_UPDATE_UPGRADE_PROMPT = gql`
	${GQL_FRAG_UPGRADE_PROMPT_SINGLE}
	mutation UpdateUpgradePrompt($upgradePromptId: String!, $upgradePrompt: UpgradePromptUpdateInput!) {
		updateOneUpgradePrompt(where: { id: $upgradePromptId }, data: $upgradePrompt) {
			...UpgradePromptSingle
		}
	}
`;

export const GQL_DELETE_UPGRADE_PROMPT = gql`
	mutation DeleteUpgradePrompt($upgradePromptId: String!) {
		deleteOneUpgradePrompt(where: { id: $upgradePromptId }) {
			id
		}
	}
`;

export const GQL_GET_UPGRADE_SUBSCRIPTION_TYPES = gql`
	${GQL_FRAG_UPGRADE_SUBSCRIPTION_TYPE}
	query UpgradeSubscriptionTypes {
		productSubscriptionTypes(orderBy: [{ product: { name: asc } }, { name: asc }]) {
			...UpgradeSubscriptionType
		}
	}
`;

export default class UpgradeService {
	constructor(
		private readonly _apolloClient: ApolloClient,
		private readonly _registerAbort?: RegisterAbortFunction,
	) {}

	// Fetches all upgrade paths with their associated products and prompt counts.
	// Used by the admin dashboard to display the list of available upgrade offers.
	public getAllPaths(): AbortableRequest<UpgradePathDiscountListFragment[]> {
		return ApolloUtils.abortableQuery<
			UpgradePathDiscountsQuery,
			UpgradePathDiscountListFragment[],
			UpgradePathDiscountsQueryVariables
		>(
			this._apolloClient,
			{
				query: GQL_GET_UPGRADE_PATH_DISCOUNTS,
			},
			(data) => data.upgradePathDiscounts,
			this._registerAbort,
		);
	}

	public getPath(upgradePathDiscountId: string): AbortableRequest<UpgradePathDiscountSingleFragment | null> {
		return ApolloUtils.abortableQuery<
			UpgradePathDiscountQuery,
			UpgradePathDiscountSingleFragment | null,
			UpgradePathDiscountQueryVariables
		>(
			this._apolloClient,
			{
				query: GQL_GET_UPGRADE_PATH_DISCOUNT,
				variables: {
					upgradePathDiscountId,
				},
			},
			(data) => data.upgradePathDiscount ?? null,
			this._registerAbort,
		);
	}

	// Creates a new upgrade path with discount rules and time windows.
	// Automatically refetches the paths list to keep the UI in sync.
	public async createPath(upgradePathDiscount: UpgradePathDiscountCreateInput) {
		const result = await this._apolloClient.mutate<
			CreateUpgradePathDiscountMutation,
			CreateUpgradePathDiscountMutationVariables
		>({
			mutation: GQL_CREATE_UPGRADE_PATH_DISCOUNT,
			variables: {
				upgradePathDiscount,
			},
			refetchQueries: [GQL_GET_UPGRADE_PATH_DISCOUNTS],
			awaitRefetchQueries: true,
		});

		if (!result.data?.createOneUpgradePathDiscount) {
			throw new Error('Failed to create upgrade path');
		}

		return result.data.createOneUpgradePathDiscount;
	}

	// Updates an existing upgrade path. Used when admins modify discount rules or time windows.
	// The admin UI checks for invalid engagements when day windows change (see UpgradePathModal).
	public async updatePath(upgradePathDiscountId: string, upgradePathDiscount: UpgradePathDiscountUpdateInput) {
		const result = await this._apolloClient.mutate<
			UpdateUpgradePathDiscountMutation,
			UpdateUpgradePathDiscountMutationVariables
		>({
			mutation: GQL_UPDATE_UPGRADE_PATH_DISCOUNT,
			variables: {
				upgradePathDiscountId,
				upgradePathDiscount,
			},
			refetchQueries: [GQL_GET_UPGRADE_PATH_DISCOUNTS],
			awaitRefetchQueries: true,
		});

		if (!result.data?.updateOneUpgradePathDiscount) {
			throw new Error('Failed to update upgrade path');
		}

		return result.data.updateOneUpgradePathDiscount;
	}

	public async deletePath(upgradePathDiscountId: string) {
		await this._apolloClient.mutate<DeleteUpgradePathDiscountMutation, DeleteUpgradePathDiscountMutationVariables>({
			mutation: GQL_DELETE_UPGRADE_PATH_DISCOUNT,
			variables: {
				upgradePathDiscountId,
			},
			refetchQueries: [GQL_GET_UPGRADE_PATH_DISCOUNTS, GQL_GET_UPGRADE_PROMPTS],
			awaitRefetchQueries: true,
		});
	}

	// Fetches all upgrade prompts with their associated paths and engagement counts.
	// Prompts are the UI layer (banners, modals, toasts) that display upgrade offers to users.
	public getAllPrompts(): AbortableRequest<UpgradePromptListFragment[]> {
		return ApolloUtils.abortableQuery<
			UpgradePromptsQuery,
			UpgradePromptListFragment[],
			UpgradePromptsQueryVariables
		>(
			this._apolloClient,
			{
				query: GQL_GET_UPGRADE_PROMPTS,
			},
			(data) => data.upgradePrompts,
			this._registerAbort,
		);
	}

	public getPrompt(upgradePromptId: string) {
		return ApolloUtils.abortableQuery<
			UpgradePromptQuery,
			UpgradePromptSingleFragment | null,
			UpgradePromptQueryVariables
		>(
			this._apolloClient,
			{
				query: GQL_GET_UPGRADE_PROMPT,
				variables: {
					upgradePromptId,
				},
			},
			(data) => data.upgradePrompt ?? null,
			this._registerAbort,
		);
	}

	public async createPrompt(upgradePrompt: UpgradePromptCreateInput) {
		const result = await this._apolloClient.mutate<
			CreateUpgradePromptMutation,
			CreateUpgradePromptMutationVariables
		>({
			mutation: GQL_CREATE_UPGRADE_PROMPT,
			variables: {
				upgradePrompt,
			},
			refetchQueries: [GQL_GET_UPGRADE_PROMPTS],
			awaitRefetchQueries: true,
		});

		if (!result.data?.createOneUpgradePrompt) {
			throw new Error('Failed to create upgrade prompt');
		}

		return result.data.createOneUpgradePrompt;
	}

	public async updatePrompt(upgradePromptId: string, upgradePrompt: UpgradePromptUpdateInput) {
		const result = await this._apolloClient.mutate<
			UpdateUpgradePromptMutation,
			UpdateUpgradePromptMutationVariables
		>({
			mutation: GQL_UPDATE_UPGRADE_PROMPT,
			variables: {
				upgradePromptId,
				upgradePrompt,
			},
			refetchQueries: [GQL_GET_UPGRADE_PROMPTS],
			awaitRefetchQueries: true,
		});

		if (!result.data?.updateOneUpgradePrompt) {
			throw new Error('Failed to update upgrade prompt');
		}

		return result.data.updateOneUpgradePrompt;
	}

	public async deletePrompt(upgradePromptId: string) {
		await this._apolloClient.mutate<DeleteUpgradePromptMutation, DeleteUpgradePromptMutationVariables>({
			mutation: GQL_DELETE_UPGRADE_PROMPT,
			variables: {
				upgradePromptId,
			},
			refetchQueries: [GQL_GET_UPGRADE_PROMPTS],
			awaitRefetchQueries: true,
		});
	}

	// Fetches all subscription types for use in prompt targeting dropdowns.
	// Used by the admin UI to let marketing teams target specific subscription types with prompts.
	public getSubscriptionTypes(): AbortableRequest<SubscriptionTypeOption[]> {
		return ApolloUtils.abortableQuery<
			UpgradeSubscriptionTypesQuery,
			SubscriptionTypeOption[],
			UpgradeSubscriptionTypesQueryVariables
		>(
			this._apolloClient,
			{
				query: GQL_GET_UPGRADE_SUBSCRIPTION_TYPES,
			},
			(data) => data.productSubscriptionTypes,
			this._registerAbort,
		);
	}

	// Checks if changing an upgrade path's day window would invalidate existing user engagements.
	// Called before updating a path to warn admins about data cleanup needs.
	// Returns counts of total and invalid engagements, plus the current and proposed windows.
	public async checkPathEngagements(
		pathId: string,
		newMinDay?: number | null,
		newMaxDay?: number | null,
	): Promise<{
		totalEngagements: number;
		invalidEngagements: number;
		invalidEngagementIds: string[];
		currentWindow: { min: number | null; max: number | null };
		newWindow: { min: number | null; max: number | null };
	}> {
		const params = new URLSearchParams();
		if (newMinDay !== undefined && newMinDay !== null) {
			params.append('newMinDay', newMinDay.toString());
		}
		if (newMaxDay !== undefined && newMaxDay !== null) {
			params.append('newMaxDay', newMaxDay.toString());
		}

		const response = await fetch(`/api/payment/upgrade-paths/${pathId}/engagements/check?${params.toString()}`);
		if (!response.ok) {
			const error = await response.json();
			throw new Error(error.message || 'Failed to check engagements');
		}
		return response.json();
	}

	// Deletes engagement records that would become invalid after changing a path's day window.
	// This cleanup ensures data integrity when admins adjust time-based discount windows.
	public async deleteInvalidEngagements(
		pathId: string,
		newMinDay?: number | null,
		newMaxDay?: number | null,
	): Promise<{ deleted: number; message: string }> {
		const params = new URLSearchParams();
		if (newMinDay !== undefined && newMinDay !== null) {
			params.append('newMinDay', newMinDay.toString());
		}
		if (newMaxDay !== undefined && newMaxDay !== null) {
			params.append('newMaxDay', newMaxDay.toString());
		}

		const response = await fetch(`/api/payment/upgrade-paths/${pathId}/engagements/invalid?${params.toString()}`, {
			method: 'DELETE',
		});
		if (!response.ok) {
			const error = await response.json();
			throw new Error(error.message || 'Failed to delete invalid engagements');
		}
		return response.json();
	}
}

export function useUpgradeService() {
	const apolloClient = useApolloClient();
	const registerAbort = useAbortRegistry();

	return useMemo(() => new UpgradeService(apolloClient, registerAbort), [apolloClient, registerAbort]);
}
